import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@business/workflows/ComponentUI/IconButton';
import { SVGAssets } from '@business/assets/vectors';
import { MediaPostHeaderProps } from '@business/interfaces/photo';
import { Avatar } from '@business/workflows/ComponentUI/Avatar/Avatar';
import {
  ContextMenu,
  ContextMenuItem
} from '@business/workflows/ComponentUI/ContextMenu';
import { useGetGroupPermissionsQuery } from '@ports/internal-services/group/groupSlice';
import { useSelector } from 'react-redux';
import { getCommonState } from '@ports/common/commonStateSlice';
import { useControls } from 'react-zoom-pan-pinch';

const iconClass = 'w-5 h-5 text-white drop-shadow-md';
const iconClassSmall = 'w-4 h-4 text-white drop-shadow-md';

const MediaPostHeader: React.FC<MediaPostHeaderProps> = ({
  onBack,
  onExpand,
  onShare,
  onInfo,
  onStar,
  onDelete,
  onDownload,
  onEdit,
  mediaType,
  isFavorite,
  isFromMemory,
  taggedPeople = [],
  canShare = false,
  canDelete = false,
  canDownload = true,
  canEdit = false,
  isPrivate = false,
  groupId,
  assetCreatedBy,
  currentUserId,
  canFavorite = false,
  isMediaOwner,
  canFullScreen = false,
  isMemoryOwner = false
}) => {
  const { t } = useTranslation();
  const { zoomIn, zoomOut } = useControls();
  const { selectedMedia } = useSelector(getCommonState);
  const isSharedWithMe = selectedMedia?.source === 'sharing-shared-with-me';

  // Only fetch group permissions if groupId is provided
  const { data: groupPermissionsData } = useGetGroupPermissionsQuery(
    { groupId: groupId || '' },
    { skip: !groupId }
  );

  const handleZoomIn = useCallback(() => {
    if (mediaType === 'image') {
      zoomIn();
    }
  }, [mediaType, zoomIn]);

  const handleZoomOut = useCallback(() => {
    if (mediaType === 'image') {
      zoomOut();
    }
  }, [mediaType, zoomOut]);

  // isOwnerAccess should:
  // - Default to true when NOT in group flow (no groupId)
  // - Check permission when in group flow (has groupId)
  const isOwnerAccess = !groupId
    ? true
    : (groupPermissionsData?.data?.includes('owner_access:true') ?? false);

  // For delete button in group flow: only show if user created the asset
  const canShowDelete = !groupId
    ? canDelete
    : canDelete && isOwnerAccess && assetCreatedBy === currentUserId;

  const actionMenuItems = [
    !isFromMemory &&
      canFullScreen && {
        id: 'expand',
        label: <ContextMenuItem label={t('mediaView.expand')} />,
        onClick: onExpand
      },
    canShare &&
      !isPrivate &&
      isMediaOwner && {
        id: 'share',
        label: <ContextMenuItem label={t('mediaView.share')} />,
        onClick: onShare
      },
    canEdit &&
      (!isFromMemory || isMemoryOwner) && {
        id: 'edit',
        label: <ContextMenuItem label={t('mediaView.info')} />,
        onClick: onEdit
      },
    mediaType === 'image' &&
      !isFromMemory && {
        id: 'zoomIn',
        label: <ContextMenuItem label={t('mediaView.zoomIn')} />,
        onClick: handleZoomIn
      },
    mediaType === 'image' &&
      !isFromMemory && {
        id: 'zoomOut',
        label: <ContextMenuItem label={t('mediaView.zoomOut')} />,
        onClick: handleZoomOut
      },
    !isFromMemory &&
      !isSharedWithMe &&
      isMediaOwner && {
        id: 'info',
        label: <ContextMenuItem label={t('mediaView.info')} />,
        onClick: onInfo
      },
    isOwnerAccess &&
      canFavorite &&
      isMediaOwner && {
        id: 'favorite',
        label: <ContextMenuItem label={t('mediaView.favorite')} />,
        onClick: onStar
      },
    canShowDelete && {
      id: 'delete',
      label: <ContextMenuItem label={t('mediaView.delete')} />,
      onClick: onDelete
    },
    !isFromMemory &&
      canDownload && {
        id: 'download',
        label: <ContextMenuItem label={t('mediaView.download')} />,
        onClick: onDownload
      }
  ].filter(Boolean) as any[];

  return (
    <header className="sticky top-0 flex items-center justify-between px-6 py-4 max-w-6xl mx-auto relative z-50 bg-[var(--media-page-bg)]">
      <div className="flex items-center justify-center gap-3">
        <IconButton
          label={t('mediaView.back')}
          className="gap-2 text-white"
          onClick={onBack}>
          <SVGAssets.BackIcon className="w-7 h-7 text-white drop-shadow-md" />
          <div>{t('mediaView.back')}</div>
        </IconButton>
        {isFromMemory && taggedPeople.length > 0 && (
          <div className="flex items-center -space-x-3">
            {taggedPeople.slice(0, 4).map((person, index) => (
              <div
                key={index}
                className="relative border-2 border-black rounded-full"
                style={{ zIndex: taggedPeople.length - index }}>
                <Avatar
                  src={person?.person?.profileImage}
                  alt={person?.person?.name || ''}
                  size="h-10 w-10"
                  rounded={true}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons - full icon row on tablet and up */}
      <div className="hidden sm:flex sm:flex-nowrap items-center gap-3 text-gray-200">
        {!isFromMemory && canFullScreen && (
          <IconButton label={t('mediaView.expand')} onClick={onExpand}>
            <SVGAssets.Expand className={iconClass} />
          </IconButton>
        )}
        {canShare && !isPrivate && isMediaOwner && (
          <IconButton label={t('mediaView.share')} onClick={onShare}>
            <SVGAssets.Share className={iconClassSmall} />
          </IconButton>
        )}
        {canEdit && (!isFromMemory || isMemoryOwner) && (
          <IconButton label={t('mediaView.info')} onClick={onEdit}>
            <SVGAssets.ProfileEditIcon className={iconClassSmall} />
          </IconButton>
        )}

        {/* Zoom buttons - only for images */}
        {mediaType === 'image' && !isFromMemory && (
          <>
            <IconButton label={t('mediaView.zoomIn')} onClick={handleZoomIn}>
              <SVGAssets.ZoomIn className={iconClassSmall} />
            </IconButton>
            <IconButton label={t('mediaView.zoomOut')} onClick={handleZoomOut}>
              <SVGAssets.ZoomOut className={iconClass} />
            </IconButton>
          </>
        )}
        {!isFromMemory && !isSharedWithMe && isMediaOwner && (
          <IconButton label={t('mediaView.info')} onClick={onInfo}>
            <SVGAssets.Info className={iconClassSmall} />
          </IconButton>
        )}
        {isOwnerAccess && canFavorite && isMediaOwner && (
          <IconButton label={t('mediaView.favorite')} onClick={onStar}>
            {isFavorite ? (
              <SVGAssets.Favorite className={iconClassSmall} />
            ) : (
              <SVGAssets.FavoriteIcons className={iconClassSmall} />
            )}
          </IconButton>
        )}
        {canShowDelete && (
          <IconButton label={t('mediaView.delete')} onClick={onDelete}>
            <SVGAssets.DeleteIcon className={iconClassSmall} />
          </IconButton>
        )}
        {!isFromMemory && canDownload && (
          <IconButton label={t('mediaView.download')} onClick={onDownload}>
            <SVGAssets.DownloadIcon className={iconClass} />
          </IconButton>
        )}
      </div>

      {/* Action buttons - collapse into a three-dot menu on phone widths only */}
      <div className="flex sm:hidden">
        <ContextMenu
          menuItems={actionMenuItems}
          icon={<SVGAssets.ThreeDots className={iconClass} />}
        />
      </div>
    </header>
  );
};

export default MediaPostHeader;
